import prebidInstance from "./prebidSetup";  
import storeAllBids from "./storeAllBids"

function formatDatetime() {
    const now = new Date();
    return now.toISOString().slice(0, 19).replace("T", " "); // Converts to "YYYY-MM-DD HH:MM:SS"
}

class BidService {
  constructor() {
    this.pbjs = prebidInstance.pbjs;  //Use singleton instance
    this.subscribers = new Map();
    this.bidCache = new Map();
    this.activeAuctions = new Set();
    this.pendingQueue = [];
    this.BID_CACHE_TTL = 50000; // Cache TTL slightly less than refresh
    this.MAX_CONCURRENT_AUCTIONS = 2;

    this.init();
  }

  init() {
    if (this.pbjs?.requestBids) {
      this.setupEventListeners();
    } else {
      this.pbjs.que?.push(() => this.setupEventListeners());
    }

  }

  setupEventListeners() {
    this.pbjs.onEvent("auctionEnd", (data) => {
      if (data?.adUnitCode) {
        this.processBidResponse(data.adUnitCode);
      }
      this.processNextInQueue();
    });
  }

  subscribe(adUnitId, callback) {
    if (!this.subscribers.has(adUnitId)) {
      this.subscribers.set(adUnitId, new Set());
    }
    this.subscribers.get(adUnitId).add(callback);

    const cachedBid = this.getCachedBid(adUnitId);
    if (cachedBid) {
      setTimeout(() => callback(cachedBid), 0);
    } else {
      this.requestBidsForAdUnit(adUnitId);
    }

    return () => {
      const subs = this.subscribers.get(adUnitId);
      if (subs) {
        subs.delete(callback);
        if (subs.size === 0) {
          this.subscribers.delete(adUnitId);
        }
      }
    };
  }

  getCachedBid(adUnitId) {
    const cached = this.bidCache.get(adUnitId);
    if (cached && Date.now() - cached.timestamp < this.BID_CACHE_TTL) {
      return cached.data;
    }
    return null;
  }


requestBidsForAdUnit(adUnitId) {
    const adUnitArray = Array.isArray(adUnitId) ? adUnitId : [adUnitId];

    // Filter out already active ad units
    const filteredIds = adUnitArray.filter(id => !this.activeAuctions.has(id));

    if (filteredIds.length === 0) return;

    // Add them to the active set to avoid duplicates
    filteredIds.forEach(id => this.activeAuctions.add(id));
    
    this.pbjs.requestBids({
        adUnitCodes: filteredIds,
        timeout: 1000,
        bidsBackHandler: () => {
            for (const id of filteredIds) {
                this.processBidResponse(id);
                this.activeAuctions.delete(id);
            }
            this.processNextInQueue();
        }
    });
}

  processBidResponse(adUnitId) {
      const responses = this.pbjs.getBidResponsesForAdUnitCode(adUnitId);
      const bids = responses?.bids || [];

      if (!bids.length) {
          console.warn(`[bidService] No valid bids for ${adUnitId}`);
          this.notifySubscribers(adUnitId, { winningAd: null, bidder: null, cpm: null, isVideo: false });
          return;
      }

      //Capture auction start time
      const auctionStartTime = formatDatetime();

      // Find the winning bid (highest CPM)
      let topBid = bids.reduce((max, bid) => (bid.cpm > max.cpm ? bid : max), bids[0]);

      // Store all bids (even losing ones) in the database
    storeAllBids(adUnitId, bids, topBid, auctionStartTime);

      // --- Native extraction/normalization ---
    let nativePayload = topBid.native || null;

    // If no structured native, try to parse topBid.ad (some bidders embed a native JSON there)
    if (!nativePayload && typeof topBid.ad === "string") {
      try {
        const parsed = JSON.parse(topBid.ad);
        nativePayload = parsed && (parsed.native || parsed);
        // sanity-check: require at least title or image to treat as native payload
        if (nativePayload && !(nativePayload.title || nativePayload.image || nativePayload.assets)) {
          nativePayload = null;
        }
      } catch (e) {
        nativePayload = null;
      }
    }

    // Normalize to the small shape your renderer expects (keep only needed fields)
    let nativeNormalized = null;
    if (nativePayload && typeof nativePayload === "object") {
      // helper to read possible variations (Prebid has many shapes)
      const readText = (v) => (v && (v.text || v)) || null;
      const readImageUrl = (img) => {
        if (!img) return null;
        if (typeof img === "string") return img;
        return img.url || img.src || (img && img.image && (img.image.url || img.image.src)) || null;
      };

      // Prebid sometimes uses top-level keys or nested asset arrays; attempt both
      const title = readText(nativePayload.title) || (nativePayload.title && readText(nativePayload.title.text)) || null;
      const body = readText(nativePayload.body) || (nativePayload.body && readText(nativePayload.body.text)) || null;
      const sponsoredBy = readText(nativePayload.sponsoredBy) || readText(nativePayload.brand) || null;
      const cta = readText(nativePayload.cta) || null;
      const image = readImageUrl(nativePayload.image) || (nativePayload.image && nativePayload.image.url) || null;
      const clickUrl = nativePayload.clickUrl || (nativePayload.link && nativePayload.link.url) || (nativePayload.action && nativePayload.action.url) || null;

      // impression trackers sometimes appear here (you said nurl is sufficient, but include if present)
      const impressionTrackers = nativePayload.impressionTrackers || nativePayload.imptrackers || nativePayload.impression_urls || null;

      nativeNormalized = {
        title: title || null,
        body: body || null,
        sponsoredBy: sponsoredBy || null,
        cta: cta || null,
        image: image || null,
        clickUrl: clickUrl || null,
        impressionTrackers: impressionTrackers || null,
        // keep raw native for debugging optionally (commented out)
        // __rawNative: nativePayload
      };
    }

    // Keep original winningAd string for legacy flow, but if nativeNormalized exists send object
    let winningAdValue = topBid.ad || topBid.vastXml || topBid.vastUrl || "";

    if (nativeNormalized) {
      winningAdValue = {
        native: nativeNormalized,

      };
    }

    // determine width/height safely (prefer explicit numbers)
    let width = null, height = null;

    const toNum = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    if (topBid.width != null && topBid.height != null) {
      width = toNum(topBid.width);
      height = toNum(topBid.height);
    } else if (topBid.size && typeof topBid.size === "string") {
      const m = topBid.size.match(/^(\d+)\s*[xX]\s*(\d+)$/);
      if (m) {
        width = toNum(m[1]);
        height = toNum(m[2]);
      }
    } else if (Array.isArray(topBid.size) && topBid.size.length >= 2) {
      width = toNum(topBid.size[0]);
      height = toNum(topBid.size[1]);
    }


      // Store only the winning bid for rendering
      const bidData = {
        winningAd: winningAdValue,
        bidder: topBid.bidder || "No Bidder",
        cpm: topBid.cpm || 0,
        isVideo: Boolean(topBid.vastXml || topBid.vastUrl),
        ad_id: topBid.adId || "none",
        nurl: topBid.nurl || topBid.nurl_url || topBid.nurlUrl || null,
        width: Number.isFinite(width) ? width : null,
        height: Number.isFinite(height) ? height : null,
      };

      //console.log(`[bidService] Storing winning bid for ${adUnitId}:`, bidData);
      this.bidCache.set(adUnitId, { data: bidData, timestamp: Date.now() });
      this.notifySubscribers(adUnitId, bidData);
  }

  notifySubscribers(adUnitId, bidData) {
    this.subscribers.get(adUnitId)?.forEach((callback) => {
      try {
        callback(bidData);
      } catch (error) {
        console.error(`[bidService] Error in subscriber callback for ${adUnitId}:`, error);
      }
    });
  }

  processNextInQueue() {
    if (this.pendingQueue.length === 0) return;

    // Take as many ad units as possible while respecting MAX_CONCURRENT_AUCTIONS
    const availableSlots = this.MAX_CONCURRENT_AUCTIONS - this.activeAuctions.size;
    if (availableSlots <= 0) return;

    const batchToProcess = this.pendingQueue.splice(0, availableSlots);

    this.requestBidsForAdUnit(batchToProcess);
}


}

const bidService = new BidService();
export default bidService;
